import { __assign, __read } from "tslib";
import { useAdminAcquiredApps } from '@office-start/admin-acquired-apps-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useIdentity } from '@office-start/identity-service';
import { useTeachingUiConfig, useTeachingUiEnabled, } from '@office-start/teaching-ui-config-service';
import { useEffect, useMemo } from 'react';
import create from 'zustand';
import { CalloutDeclarations } from '../configuration';
import { getValidSequences } from '../getSequence';
import { useTuiServiceStore } from '../store';
import { useExtraActivationData } from './useExtraActivationData';
var useFirstLoadSetupStore = create(function (set) { return ({
    firstLoadSequenceMarked: false,
    setFirstLoadSequenceMarked: function (firstLoadSequenceMarked) {
        set({ firstLoadSequenceMarked: firstLoadSequenceMarked });
    },
    setupStarted: false,
    setSetupStarted: function (setupStarted) {
        set({ setupStarted: setupStarted });
    },
    setupCompleted: false,
    setSetupCompleted: function (setupCompleted) {
        set({ setupCompleted: setupCompleted });
    },
    setUserProfileService: function (userProfileService) {
        set({ userProfileService: userProfileService });
    },
    userProfileService: undefined,
}); });
export var useSingletonFirstLoadSetup = function () {
    var enabled = useTeachingUiEnabled();
    var config = useTeachingUiConfig().data;
    var _a = useFirstLoadSetupStore(), firstLoadSequenceMarked = _a.firstLoadSequenceMarked, setFirstLoadSequenceMarked = _a.setFirstLoadSequenceMarked, userProfileService = _a.userProfileService, setUserProfileService = _a.setUserProfileService, setupCompleted = _a.setupCompleted, setSetupCompleted = _a.setSetupCompleted, setupStarted = _a.setupStarted, setSetupStarted = _a.setSetupStarted;
    var _b = __read(useTuiServiceStore(function (state) { return [state.setActiveTeachingSequence, state.setDisabled]; }), 2), setActiveTeachingSequence = _b[0], setDisabled = _b[1];
    var identity = useIdentity().data;
    var context = useOfficeComponentContext().data;
    var featureGates = useFeatureGates().data;
    var extraSequenceValidationData = useExtraActivationData();
    var setDismissedTimestamp = useAdminAcquiredApps().setDismissedTimestamp;
    useEffect(function () {
        if (setupStarted ||
            setupCompleted ||
            !context ||
            !identity ||
            !(featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) ||
            !userProfileService ||
            !enabled ||
            !config ||
            !extraSequenceValidationData) {
            return;
        }
        // Mark as started so we don't do an async operation twice
        setSetupStarted(true);
        getValidSequences({
            context: context,
            teachingUiConfig: config,
            identity: identity,
            featureGates: featureGates.result,
            userProfileService: userProfileService,
            extraSequenceValidationData: extraSequenceValidationData,
        }).then(function (_a) {
            var items = _a.items, impressionEvent = _a.impressionEvent, shouldUpdateNewUserState = _a.shouldUpdateNewUserState, startingIndex = _a.startingIndex;
            if (items === null || items === void 0 ? void 0 : items.length) {
                var markSequencesAsSeen = function () {
                    if (!firstLoadSequenceMarked) {
                        if (impressionEvent) {
                            // Used for marking the new user callout as seen
                            userProfileService.incrementMetric(impressionEvent);
                        }
                        // If you see the first callout you "see" all of them
                        items.forEach(function (s) {
                            if (s.impressionEvent) {
                                userProfileService.incrementMetric(s.impressionEvent);
                            }
                        });
                        setFirstLoadSequenceMarked(true);
                    }
                };
                var getCallout_1 = function (calloutId, sequenceId, actionButton, onDismiss) { return (__assign({ id: calloutId, sequenceId: sequenceId, actionButton: actionButton, onDismiss: onDismiss }, CalloutDeclarations[calloutId])); };
                var getCalloutArrayFromTuiItem_1 = function (item) {
                    switch (item.type) {
                        case 'item':
                            return [getCallout_1(item.callout, item.id)];
                        case 'group':
                            return item.callouts.map(function (calloutId) {
                                return getCallout_1(calloutId, item.id);
                            });
                        case 'standalone':
                            return [
                                getCallout_1(item.callout, item.id, item.actionButton, function () {
                                    var _a;
                                    return (_a = item.onDismiss) === null || _a === void 0 ? void 0 : _a.call(item, {
                                        setDismissedTimestamp: setDismissedTimestamp,
                                    });
                                }),
                            ];
                    }
                };
                var callouts = items.reduce(function (currentCalloutList, tuiItem) {
                    return currentCalloutList.concat(getCalloutArrayFromTuiItem_1(tuiItem));
                }, []);
                setActiveTeachingSequence(callouts, markSequencesAsSeen, startingIndex, shouldUpdateNewUserState);
            }
            else {
                // If there are no sequences, mark as completed so we don't do an async operation twice
                setDisabled();
            }
            setSetupCompleted(true);
        });
    }, [
        context,
        identity,
        userProfileService,
        featureGates,
        setupStarted,
        setSetupStarted,
        setupCompleted,
        setSetupCompleted,
        firstLoadSequenceMarked,
        setFirstLoadSequenceMarked,
        config,
        enabled,
        setDismissedTimestamp,
        setActiveTeachingSequence,
        setDisabled,
        extraSequenceValidationData,
    ]);
    return useMemo(function () { return [setupCompleted, setUserProfileService]; }, [setupCompleted, setUserProfileService]);
};
//# sourceMappingURL=useSingletonFirstLoadSetup.js.map